import React from 'react';
import {CheckCircle, XCircle, AlertTriangle, Info, X} from 'lucide-react';
import {useAdminNotifications, Notification, NotificationType} from '../../contexts/AdminNotificationContext';

interface NotificationBannerProps {
    notification: Notification;
}

const getNotificationStyles = (type: NotificationType) => {
    switch (type) {
        case 'success':
            return {
                containerClass: 'bg-green-50 border-green-200 text-green-800',
                iconClass: 'text-green-600',
                icon: CheckCircle,
            };
        case 'error':
            return {
                containerClass: 'bg-red-50 border-red-200 text-red-800',
                iconClass: 'text-red-600',
                icon: XCircle,
            };
        case 'warning':
            return {
                containerClass: 'bg-yellow-50 border-yellow-200 text-yellow-800',
                iconClass: 'text-yellow-600',
                icon: AlertTriangle,
            };
        case 'info':
            return {
                containerClass: 'bg-blue-50 border-blue-200 text-blue-800',
                iconClass: 'text-blue-600',
                icon: Info,
            };
        default:
            return {
                containerClass: 'bg-gray-50 border-gray-200 text-gray-800',
                iconClass: 'text-gray-600',
                icon: Info,
            };
    }
};

const NotificationBanner: React.FC<NotificationBannerProps> = ({notification}) => {
    const {dismissNotification} = useAdminNotifications();
    const {containerClass, iconClass, icon: Icon} = getNotificationStyles(notification.type);

    return (
        <div className={`border rounded-lg p-4 mb-4 ${containerClass}`}>
            <div className="flex items-start">
                <Icon className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${iconClass}`} />
                <div className="flex-1">
                    {notification.title && <h3 className="text-sm font-medium mb-1">{notification.title}</h3>}
                    <p className="text-sm">{notification.message}</p>
                </div>
                <button
                    onClick={() => dismissNotification(notification.id)}
                    className="ml-3 flex-shrink-0 hover:opacity-70 transition-opacity"
                    aria-label="Dismiss notification">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

interface AdminNotificationBannerProps {
    className?: string;
}

export const AdminNotificationBanner: React.FC<AdminNotificationBannerProps> = ({className = ''}) => {
    const {notifications} = useAdminNotifications();

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div className={`space-y-2 ${className}`}>
            {notifications.map((notification) => (
                <NotificationBanner key={notification.id} notification={notification} />
            ))}
        </div>
    );
};

export default AdminNotificationBanner;
